import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { userHasRight } from "selectors/rights";
import { TIME_REVIEW_DOWNLOAD, QA_PAYROLL_PAGE_DIRECTOR_DROPDOWN_VIEW } from "typeConstants/rights";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";
import { MSpinner, MSplitter } from "components/Shared";
import EmployeeListCard from "components/Shared/TimeLogs/EmployeeListCard";
import { getManagers } from "actions/qualityAssurance";
import { getPaySummary } from "actions/homeowner/fieldReps";
import { getFieldRepManagers, getInspectorListSummary } from "actions/inspectorTimeCard";
import { getEmployeesByPayPeriod, approvePayPeriodUser, getQaEmployeesByPayPeriod } from "actions/account";
import { getPayPeriodList } from "actions/casetimelog";
import { getAppraiserManagers } from "requests/appraiser";
import * as employeeRoles from "typeConstants/employeeRoles";
import {
  busOpsEmployeeColDefs,
  appraiserEmployeeColDefs,
  fieldRepEmployeeColDefs,
  saEmployeeColDefs,
  isTotalRow,
} from "components/Shared/TimeLogs/Definitions";
import FieldRepTimeLogDetails from "components/EmployeeTimeLogs/FieldRepTimeLogDetails";
import QualityAssuranceTimeLogDetails from "components/EmployeeTimeLogs/QualityAssuranceTimeLogDetails";
import AppraiserTimeLogDetails from "components/EmployeeTimeLogs/AppraiserTimeLogDetails";
import { getEmployeesByRoles } from "requests/employee";
import SchedulingAssistantTimelogDetails from "components/CallCenter/SchedulingAssistant/SchedulingAssistantTimeLogDetails.tsx";
import { getSAEmployeeList } from "requests/schedulingTimeCard/index.ts";

const EmployeeTimeLogs = ({
  employeeRole,
  getBusOpsManagers,
  busOpsManagersList,
  isFetchingBusOpsManagersList,
  fieldRepManagers,
  getFieldRepManagers,
  isFetchingFieldRepManagers,
  getEmployeesByPayPeriod,
  payPeriods,
  isFetchingPayPeriods,
  employees,
  isFetchingEmployees,
  approvePayPeriodUser,
  isApprovingPayPeriod,
  getPayPeriodList,
  profile,
  getInspectorListSummary,
  fieldRepList,
  isFetchingFieldRepList,
  getAppraiserManagers,
  fetchingAppraiserManagers,
  appraiserManagers,
  getQaEmployeesByPayPeriod,
  canDownload,
  canViewQadDropdown,
}) => {
  const [saEmployeeData, setSAEmployeeData] = useState(); // type SchedulingTimeCardResponse

  const [selectedEmployee, setSelectedEmployee] = useState();
  const [selectedPayPeriod, setSelectedPayPeriod] = useState();
  const [fieldDevManagers, setFieldDevManagers] = useState([]);

  const [fieldOpsDirectors, setFieldOpsDirectors] = useState();
  const [fieldOpsManagers, setFieldOpsManagers] = useState();
  const [fieldSchedulingManagers, setFieldSchedulingManagers] = useState();
  const [selectedFieldDevIndex, setSelectedFieldDevIndex] = useState(null);
  const [selectedFieldOpsDirectorIndex, setSelectedFieldOpsDirectorIndex] = useState(null);
  const [selectedFieldOpsManagerIndex, setSelectedFieldOpsManagerIndex] = useState(null);
  const [qads, setQads] = useState(null);

  const [isTrainee, setIsTrainee] = useState(null);

  const mounted = useRef(true);

  useEffect(() => {
    getPayPeriodList();
    getEmployeesByRoles([employeeRoles.FIELD_DEVELOPMENT_MANAGER], setFieldDevManagers, mounted); // FDM

    if (employeeRole === employeeRoles.FIELD_SERVICE_REP) {
      getEmployeesByRoles([employeeRoles.FIELD_OPERATIONS_DIRECTOR], setFieldOpsDirectors, mounted); // Logistics Manager - call it Field Manager (chris says so)
      getEmployeesByRoles([employeeRoles.FIELD_OPERATIONS_MANAGER], setFieldOpsManagers, mounted); // Field Director
    }

    if (employeeRole === employeeRole.CUSTOMER_EXPERIENCE_REP) {
      getSAManagers();
    }

    if (employeeRole === employeeRoles.BUSOP_AGENT && canViewQadDropdown) {
      getEmployeesByRoles([employeeRoles.BUSOP_DIRECTOR], setQads, mounted);
    }
    return () => (mounted.current = false);
  }, []);

  const getSAManagers = () => {
    setFieldSchedulingManagers(null);
    getEmployeesByRoles([employeeRoles.CUSTOMER_EXPERIENCE_MANAGER], setFieldSchedulingManagers, mounted);
  };

  useEffect(() => {
    if (!selectedPayPeriod && payPeriods.length) {
      setSelectedPayPeriod(payPeriods[0]);
    }
  }, [payPeriods]);

  const mapEmployeeList = (list, setFunction) => [
    { primaryText: "Clear Selection", onClick: () => setFunction(null) },
    ...list.map((e, i) => ({ primaryText: e.firstLastName, onClick: () => setFunction(i) })),
  ];

  const hasEnoughFilters = () =>
    selectedFieldDevIndex !== null || selectedFieldOpsDirectorIndex !== null || selectedFieldOpsManagerIndex !== null;

  const getInspectorListSummaryWrapped = () => {
    if (selectedPayPeriod && fieldDevManagers && fieldOpsDirectors && fieldOpsManagers) {
      let fieldDevManager = fieldDevManagers[selectedFieldDevIndex]?.internetUserId;
      let fieldOpsDirector = fieldOpsDirectors[selectedFieldOpsDirectorIndex]?.internetUserId;
      let fieldOpsManager = fieldOpsManagers[selectedFieldOpsManagerIndex]?.internetUserId;

      if (hasEnoughFilters()) {
        getInspectorListSummary(selectedPayPeriod.id, fieldOpsManager, fieldOpsDirector, fieldDevManager, isTrainee, [
          employeeRoles.FIELD_SERVICE_REP,
        ]);
      }
    }
  };

  useEffect(() => {
    getInspectorListSummaryWrapped();
  }, [
    selectedFieldOpsManagerIndex,
    selectedFieldOpsDirectorIndex,
    selectedFieldDevIndex,
    isTrainee,
    selectedPayPeriod,
  ]);

  // items should be { primaryText, onClick }[]
  const makeEmployeeListStyleDropDown = (defaultText, items, selectedIndex, hasClearOption = true) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleItemClick = (item) => {
      if (item.onClick) item.onClick();
      handleClose();
    };

    return (
      <div className="my-time-log-filter">
        <Button
          onClick={handleClick}
          endIcon={<Icon>arrow_drop_down</Icon>}
        >
          {/* add 1 to the index if we have a clear option at the top of the list */}
          {items && items.length && selectedIndex !== null && selectedIndex > -1
            ? items[selectedIndex + hasClearOption].primaryText
            : defaultText}
        </Button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
        >
          {items?.map((item, index) => (
            <MenuItem key={index} onClick={() => handleItemClick(item)}>
              {item.primaryText}
            </MenuItem>
          ))}
        </Menu>
      </div>
    );
  };

  const getExtraFilters = () => {
    if (!fieldDevManagers || !fieldOpsManagers || !fieldOpsDirectors) return null;

    let traineeOptions = [
      { primaryText: "Clear Selection", onClick: () => setIsTrainee(null) },
      { primaryText: "Is Trainee", onClick: () => setIsTrainee(true) },
      { primaryText: "Is Not Trainee", onClick: () => setIsTrainee(false) },
    ];

    // the index will be increased by one in the makeEmployeeListStyleDropDown function  accounting for the clear option
    let traineeIndex = null;
    if (isTrainee === true) traineeIndex = 0;
    else if (isTrainee === false) traineeIndex = 1;

    return (
      <>
        {makeEmployeeListStyleDropDown(
          "Field Director",
          mapEmployeeList(fieldOpsDirectors, setSelectedFieldOpsDirectorIndex),
          selectedFieldOpsDirectorIndex
        )}
        {makeEmployeeListStyleDropDown(
          "Field Manager",
          mapEmployeeList(fieldOpsManagers, setSelectedFieldOpsManagerIndex),
          selectedFieldOpsManagerIndex
        )}
        {makeEmployeeListStyleDropDown(
          "FDM",
          mapEmployeeList(fieldDevManagers, setSelectedFieldDevIndex),
          selectedFieldDevIndex
        )}
        {makeEmployeeListStyleDropDown("Trainee?", traineeOptions, traineeIndex)}
      </>
    );
  };

  const getSAData = (internetUserId, payPeriodId) => {
    setSAEmployeeData(null);
    getSAEmployeeList(internetUserId, payPeriodId, setSAEmployeeData, mounted);
  };

  let timeLogApi = {};
  switch (employeeRole) {
    case employeeRoles.BUSOP_AGENT: // QA
    case employeeRoles.QUALITY_COMPLIANCE_AUDITOR:
    case employeeRoles.VALUATION_QUALITY_ASSOCIATE:
      timeLogApi = {
        managers: busOpsManagersList,
        getManagers: () => getBusOpsManagers(),
        isFetchingManagers: isFetchingBusOpsManagersList,
        getData: (internetUserId, payPeriodId, traineeFilterOption, statusFilterOption) => getQaEmployeesByPayPeriod(internetUserId, payPeriodId, traineeFilterOption, statusFilterOption),
        columnDefinitions: busOpsEmployeeColDefs,
        detailsComponent: (
          <QualityAssuranceTimeLogDetails selectedPayPeriod={selectedPayPeriod} selectedEmployee={selectedEmployee} />
        ),
        listData: employees,
        isFetchingListData: isFetchingEmployees,
      };
      break;
    case employeeRoles.FIELD_SERVICE_REP:
      timeLogApi = {
        managers: fieldDevManagers.length && fieldRepManagers.length ? fieldRepManagers.concat(fieldDevManagers) : [],
        getManagers: () => getFieldRepManagers(),
        isFetchingManagers: isFetchingFieldRepManagers,
        getData: () => {}, // no-op function because this is handled in use effect calling getInspectorListSummaryWrapped
        columnDefinitions: fieldRepEmployeeColDefs,
        detailsComponent: (
          <FieldRepTimeLogDetails selectedPayPeriod={selectedPayPeriod} selectedEmployee={selectedEmployee} />
        ),
        listData: hasEnoughFilters() && fieldRepList,
        isFetchingListData: isFetchingFieldRepList,
        extraFilters: getExtraFilters(),
      };
      break;
    case employeeRoles.VALUATION_APPRAISER:
      timeLogApi = {
        managers: Array.isArray(appraiserManagers) ? appraiserManagers : [],
        getManagers: () => getAppraiserManagers(),
        isFetchingManagers: fetchingAppraiserManagers,
        getData: (internetUserId, payPeriodId) =>
          getEmployeesByPayPeriod(internetUserId, payPeriodId, [employeeRoles.VALUATION_APPRAISER]),
        columnDefinitions: appraiserEmployeeColDefs,
        detailsComponent: (
          <AppraiserTimeLogDetails selectedPayPeriod={selectedPayPeriod} selectedEmployee={selectedEmployee} />
        ),
        listData: employees,
        isFetchingListData: isFetchingEmployees,
      };
      break;
    case employeeRoles.CUSTOMER_EXPERIENCE_REP:
      timeLogApi = {
        managers: fieldSchedulingManagers ?? [],
        getManagers: getSAManagers,
        isFetchingManagers: !fieldSchedulingManagers,
        getData: getSAData,
        columnDefinitions: saEmployeeColDefs,
        detailsComponent: (
          <SchedulingAssistantTimelogDetails
            selectedPayPeriod={selectedPayPeriod}
            selectedEmployee={selectedEmployee}
          />
        ),
        listData: saEmployeeData?.employeeList,
        totalsData: saEmployeeData?.totals,
        isFetchingListData: !saEmployeeData,
        getDownloadFileName: (managerLabel, payPeriodLabel) =>
          `Scheduling Assistant Payroll for ${managerLabel} ${payPeriodLabel}`,
      };
      break;
  }

  const employeeSelected = (id, data) => {
    if (data) {
      setSelectedEmployee({
        internetUserId: id,
        employeeId: data.employeeId,
        name: data.employeeName,
        number: data.inspectorNumber ?? data.appraiserNumber,
        isTeamLeader: data.isTeamLeader,
      });
    }
  };
  const payPeriodSelected = (i) => {
    setSelectedPayPeriod(payPeriods[i]);
  };
  const onManagerChange = () => setSelectedEmployee(null);

  if (isFetchingPayPeriods || !payPeriods.length) {
    return <MSpinner />;
  }

  return (
    <MSplitter
      className={selectedEmployee ? "employee-timelogs field-rep-time-logs" : "padding-page-content full-height"}
      firstChild={
        // TODO: fix this mess of nested divs and styles...
        <div
          className="employee-timelogs-grid"
          style={
            employeeRole === employeeRoles.BUSOP_AGENT 
              || employeeRole === employeeRoles.VALUATION_QUALITY_ASSOCIATE 
              || employeeRole === employeeRoles.QUALITY_COMPLIANCE_AUDITOR 
              ? { height: "unset" } : null
          }
        >
          <div className="list-grid">
            <EmployeeListCard
              data={timeLogApi.listData}
              dataFilter={(row) => !isTotalRow(row)}
              getData={timeLogApi.getData}
              isFetchingData={timeLogApi.isFetchingListData}
              setInternetUserId={employeeSelected}
              payPeriods={payPeriods}
              payPeriodChangeCallback={payPeriodSelected}
              approve={approvePayPeriodUser}
              isApproving={isApprovingPayPeriod}
              managers={timeLogApi.managers}
              isFetchingManagers={timeLogApi.isFetchingManagers}
              getManagers={timeLogApi.getManagers}
              profile={profile}
              getColumnDefinitions={timeLogApi.columnDefinitions}
              onManagerChangeCallback={onManagerChange}
              extraFilters={timeLogApi.extraFilters}
              totalsData={timeLogApi.totalsData}
              getDownloadFileName={timeLogApi.getDownloadFileName}
              employeeRole={employeeRole}
              canDownload={canDownload}
              qadList={qads}
            />
          </div>
        </div>
      }
      secondChild={
        selectedEmployee && (
          <>
            {/* <Grid stacked={false} noSpacing={false} style={{ marginTop: 10, paddingTop: 0 }}> */}
            {selectedEmployee && timeLogApi.detailsComponent}
          </>
        )
      }
      secondChildTitle={{
        title: "Time Log",
        buttons: [{ icon: "close", onClick: () => setSelectedEmployee(null) }],
      }}
    />
  );
};

EmployeeTimeLogs.propTypes = {
  employeeRole: PropTypes.number, // Expects one of the following values from typeConstants/employeeRoles.js: BUSOP_AGENT, VALUATION_APPRAISER, FIELD_SERVICE_REP
  // Dispatch and State props
  getBusOpsManagers: PropTypes.func,
  busOpsManagersList: PropTypes.array,
  isFetchingBusOpsManagersList: PropTypes.bool,
  getFieldRepManagers: PropTypes.func,
  fieldRepManagers: PropTypes.array,
  isFetchingFieldRepManagers: PropTypes.bool,
  getEmployeesByPayPeriod: PropTypes.func,
  getPayPeriodList: PropTypes.func,
  payPeriods: PropTypes.array,
  isFetchingPayPeriods: PropTypes.bool,
  employees: PropTypes.array,
  isFetchingEmployees: PropTypes.bool,
  profile: PropTypes.object.isRequired,
  getPaySummary: PropTypes.func.isRequired,
  getInspectorListSummary: PropTypes.func,
  isApprovingPayPeriod: PropTypes.bool,
  approvePayPeriodUser: PropTypes.func,
  fieldRepList: PropTypes.array,
  isFetchingFieldRepList: PropTypes.bool,
  appraiserManagers: PropTypes.array,
  fetchingAppraiserManagers: PropTypes.bool,
  getAppraiserManagers: PropTypes.func,
  getQaEmployeesByPayPeriod: PropTypes.func,
  canDownload: PropTypes.bool,
  canViewQadDropdown: PropTypes.bool,
};

const mapStateToProps = (state) => {
  return {
    busOpsManagersList: state.qualityAssurance.managers,
    isFetchingBusOpsManagersList: state.qualityAssurance.isFetchingManagers,
    fieldRepManagers: state.fieldRep.fieldRepManagers,
    isFetchingFieldRepManagers: state.fieldRep.isFetchingFieldRepManagers,
    payPeriods: state.caseTimeLog.payPeriods,
    isFetchingPayPeriods: state.caseTimeLog.isFetchingPayPeriods,
    employees: state.user.employees,
    isFetchingEmployees: state.user.isFetchingEmployees,
    profile: state.user.profile,
    isApprovingPayPeriod: state.user.isApprovingPayPeriod,
    fieldRepList: state.fieldRep.timeLogSummaryList,
    isFetchingFieldRepList: state.fieldRep.isFetchingTimeLogSummary,
    appraiserManagers: state.appraiser.appraiserManagers?.items,
    fetchingAppraiserManagers: state.appraiser.fetchingAppraiserManagers,
    canDownload: userHasRight(TIME_REVIEW_DOWNLOAD)(state),
    canViewQadDropdown: userHasRight(QA_PAYROLL_PAGE_DIRECTOR_DROPDOWN_VIEW)(state)
  };
};

const mapDispatchToProps = (dispatch) => ({
  getPaySummary(inspectorNumber, payPeriodId) {
    dispatch(getPaySummary(inspectorNumber, payPeriodId));
  },
  approvePayPeriodUser(payPeriodId, internetUserId) {
    dispatch(approvePayPeriodUser(payPeriodId, internetUserId));
  },
  getBusOpsManagers() {
    dispatch(getManagers());
  },
  getEmployeesByPayPeriod(internetUserId, payPeriodId, roles) {
    dispatch(getEmployeesByPayPeriod(internetUserId, payPeriodId, roles));
  },
  getPayPeriodList() {
    dispatch(getPayPeriodList());
  },
  getFieldRepManagers() {
    dispatch(getFieldRepManagers());
  },
  getInspectorListSummary(
    payPeriodId,
    operationsManagerInternetUserId,
    operationsDirectorInternetUserId,
    fieldDevelopmentManagerInternetUserId,
    isTrainee,
    roles
  ) {
    dispatch(
      getInspectorListSummary(
        payPeriodId,
        operationsManagerInternetUserId,
        operationsDirectorInternetUserId,
        fieldDevelopmentManagerInternetUserId,
        isTrainee,
        roles
      )
    );
  },
  getAppraiserManagers() {
    dispatch(getAppraiserManagers());
  },
  getQaEmployeesByPayPeriod(internetUserId, payPeriodId, traineeFilterOption, statusFilterOption) {
    dispatch(getQaEmployeesByPayPeriod(internetUserId, payPeriodId, traineeFilterOption, statusFilterOption));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeTimeLogs);

export const getPayPeriodStartDate = (fromDate) => new Date(fromDate.year, fromDate.month - 1, fromDate.day);
export const getPayPeriodEndDate = (toDate) => new Date(toDate.year, toDate.month - 1, toDate.day);
